import React, { useEffect, useState } from 'react'
import { ReportHeading } from '../common/ReportHeading'
import { Item } from '../../../common/snippets'
import { Box, Button } from '@mui/material'
import Datepicker from '../../../common/datePicker'
import DropDownOA from '../../../common/dropDownOA'
import { SplitButton } from '../../../common/SplitButton'
import * as sx from '../../../common/stylesInline/report_daily_branch'
import { useSelector } from 'react-redux'
import moment from 'moment'
import { useAtom, useSetAtom } from 'jotai'
import { BranchBalanceReportData, fetchBranchBalanceData } from '../../../common/jotaiState/reportStateAtom'
import { removeDuplicatesFromArray } from '../../../common/helper'
import { urls } from '../../../common/constants'
import { GetRequest } from '../../../common/api'
import { alertAtom, globalLoaderAtom } from '../../../common/jotaiState/globalStateAtoms'

const BranchBalanceReportNew = () => {
    const common = useSelector((state) => state?.common)
    const { loginData } = common
    const reportName = 'Branch Balance'
    const setAlert = useSetAtom(alertAtom)
    const [reportView, setReportView] = useState(false)
    const [fromDate, setFromDate] = useState(moment(new Date()))
    const [toDate, setToDate] = useState(moment(new Date()))
    const [branchStateData, setBranchStateData] = useState([])
    const [selBranchStateData, setSelBranchStateData] = useState('')
    const [branchCityData, setBranchCityData] = useState([])
    const [selBranchCityData, setSelBranchCityData] = useState('')
    const [branchData, setBranchData] = useState([])
    const [selBranchData, setSelBranchData] = useState('')
    const [branchBalanceFilterData] = useAtom(BranchBalanceReportData)
    const [, fetchBranchBalance] = useAtom(fetchBranchBalanceData)
    const [, setLoader] = useAtom(globalLoaderAtom)

    // ---------------- STATE LIST ----------------
    useEffect(() => {
        if (branchBalanceFilterData.length > 0) {
            const stateList = [
                { StateName: "All", StateID: 0 },
                ...branchBalanceFilterData.reduce((acc, val) => {
                    if (!acc.some(s => s.StateID === val.StateID)) {
                        acc.push({ StateName: val.StateName, StateID: val.StateID });
                    }
                    return acc;
                }, [])
            ];
            setBranchStateData(stateList);
            setSelBranchStateData(stateList[0]);
        } else {
            setBranchStateData([]);
            setSelBranchStateData('');
        }
    }, [branchBalanceFilterData]);

    // ---------------- CITY LIST (depends on state) ----------------
    useEffect(() => {
        if (!selBranchStateData) return;
        const filteredCities = [
            { CityName: "All", CityID: 0 },
            ...branchBalanceFilterData
                .filter(val => selBranchStateData.StateID === 0 || val.StateID === selBranchStateData.StateID)
                .reduce((acc, val) => {
                    if (!acc.some(c => c.CityID === val.CityID)) {
                        acc.push({ CityName: val.CityName, CityID: val.CityID });
                    }
                    return acc;
                }, [])
        ];
        setBranchCityData(filteredCities);

        // Default select → login city if present in list, else first
        const loginCity = filteredCities.find(c => c.CityID === loginData.intBranchCityID);
        setSelBranchCityData(loginCity || filteredCities[0]);
    }, [selBranchStateData, branchBalanceFilterData]);

    // ---------------- BRANCH LIST (depends on city) ----------------
    // ---------------- BRANCH LIST (depends on state + city) ----------------
    useEffect(() => {
        if (!selBranchStateData || !selBranchCityData) return;

        const filteredBranches = [
            { BranchName: "All", BranchID: 0 },
            ...branchBalanceFilterData
                .filter(val => {
                    // State filter
                    const matchState =
                        selBranchStateData.StateID === 0 || val.StateID === selBranchStateData.StateID;

                    // City filter
                    const matchCity =
                        selBranchCityData.CityID === 0 || val.CityID === selBranchCityData.CityID;

                    return matchState && matchCity;
                })
                .reduce((acc, val) => {
                    if (!acc.some(b => b.BranchID === val.BranchID)) {
                        acc.push({ BranchName: val.BranchName, BranchID: val.BranchID });
                    }
                    return acc;
                }, [])
        ];

        setBranchData(filteredBranches);

        // Default select → login branch if present in list, else first
        const loginBranch = filteredBranches.find(b => b.BranchID === loginData.intBranchID);
        setSelBranchData(loginBranch || filteredBranches[0]);
    }, [selBranchStateData, selBranchCityData, branchBalanceFilterData]);

    const handleGoClick = async () => {
        try {
            const obj = {
                CompanyID: loginData?.intCompanyID,
                FromDate: moment(fromDate).format('YYYY-MM-DD'),
                ToDate: moment(toDate).format('YYYY-MM-DD'),
            };
            await fetchBranchBalance(obj, 'filterData');
        } catch (error) {
            console.error("Error fetching Branch Balance Report:", error);
        }
    };

    const criteriaHeaders = [
        { field: 'fromDate', header: 'From Date', textAlign: 'left' },
        { field: 'toDate', header: 'To Date', textAlign: 'left' },
        { field: 'branchState', header: 'Branch State', textAlign: 'left' },
        { field: 'branchCity', header: 'Branch City', textAlign: 'left' },
        { field: 'branchName', header: 'Branch Name', textAlign: 'left' },
    ]

    const criteriaData = [{
        fromDate: moment(fromDate).format('DD-MMM-YYYY'),
        toDate: moment(toDate).format('DD-MMM-YYYY'),
        branchState: selBranchStateData?.StateName || '',
        branchCity: selBranchCityData?.CityName || '',
        branchName: selBranchData?.BranchName || '',
        dataType: 'entry',
    }]

    const branchBalanceColumn = [
        { headerName: 'SN', exportHead: 'SN', field: 'SN', xlCol: { dataType: 'string', dataFormatter: null }, minWidth: "20px", align: "left" },
        { headerName: 'Branch State', exportHead: 'Branch State', field: 'StateName', xlCol: { dataType: 'string', dataFormatter: null }, minWidth: "100px", align: "left", isSortIcon: true, },
        { headerName: 'Branch City', exportHead: 'Branch City', field: 'CityName', xlCol: { dataType: 'string', dataFormatter: null }, minWidth: "80px", align: "left", isSortIcon: true, },
        { headerName: 'Branch Name', exportHead: 'Branch Name', field: 'BranchName', xlCol: { dataType: 'string', dataFormatter: null }, minWidth: "80px", align: "left", isSortIcon: true },
        { headerName: 'Opening Balance', exportHead: 'Opening Balance', field: 'OpeningBalance', xlCol: { dataType: 'number', dataFormatter: null }, minWidth: "80px", align: "right", isSortIcon: true, },
        { headerName: 'Closing Balance', exportHead: 'Closing Balance', field: 'ClosingBalance', xlCol: { dataType: 'number', dataFormatter: null }, minWidth: "80px", align: "right", isSortIcon: true, },
    ]

    const getMainHeader = (columns = []) =>
        columns.map(col => {
            const header = col.headerName || col.header || ''
            return {
                ...col,
                header,
                exportHead: col.exportHead || header,
                align: col.align,
                weight: 80,
                dataType: 'header',
            }
        })

    const generateReport = async () => {
        try {
            const obj = {
                CompanyID: loginData?.intCompanyID,
                FromDate: moment(fromDate).format('YYYY-MM-DD'),
                ToDate: moment(toDate).format('YYYY-MM-DD'),
                intStateID: selBranchStateData.StateID,
                intCityID: selBranchCityData.CityID,
                intBranchID: selBranchData.BranchID,
            }
            setLoader(true)
            const res = await GetRequest(urls.getBranchBalanceReport + '?', obj)
            setLoader(false)

            if (res?.status !== 'Success' || !res.Data.table?.length) {
                setAlert({ show: true, msg: 'No data found' })
                return
            }

            const tableData = res.Data.table.map((val, i) => ({ ...val, SN: i + 1, dataType: 'entry' }))

            const reportPayload = {
                reportName: `${reportName} Report`,
                criteriaHeaders,
                criteriaData,
                mainHeader: getMainHeader(branchBalanceColumn),
                tableData,
                serialNokey: 'SN',
            }

            const rid = Date.now().toString()
            if (!window.generatedReports) window.generatedReports = {}
            window.generatedReports[rid] = reportPayload
            window.open(`/view-report?rid=${rid}`, '_blank')

        } catch (error) {
            console.error('Error fetching Branch Balance Report:', error)
            setLoader(false)
        }
    }

    return (
        <>
            <Item sx={{ padding: '0' }} className="reports">
                <ReportHeading reportName={reportName} />
                <Box sx={{ ...sx.reportFlex, justifyContent: 'start' }}>
                    <Item sx={sx.itemsxGrow}>
                        <DropDownOA
                            id="selectCompnay"
                            label="Selected Company"
                            defaultvalue={loginData.strCompanyName}
                            disabled={true}
                            // onSelect={(val) => setSelCompany(val)}
                            width="100%"
                        />
                    </Item>
                    <Item sx={sx.itemsx} className="input--container bookingDatePicker">
                        <Datepicker
                            label={`From Date`}
                            name={'from_date'}
                            id={'fromDate'}
                            value={fromDate}
                            dateChange={(date) => {
                                setFromDate(date)
                                setReportView(false)
                            }}
                            width="300px"
                        />
                    </Item>
                    <Item sx={sx.itemsx} className="input--container bookingDatePicker">
                        <Datepicker
                            label={'To Date'}
                            name={'to_date'}
                            id={'toDate'}
                            value={toDate}
                            dateChange={(date) => {
                                setToDate(date)
                                setReportView(false)
                            }}
                            width="300px"
                        />
                    </Item>
                    <Item sx={sx.itemsx}>
                        <Button
                            id="branchBalanceGoBtn"
                            variant="contained"
                            className="go click-button"
                            onClick={handleGoClick}
                        // onKeyDown={(e) => navigation3(e, 'sourceCity', 'fromDate', handleGoClick)}
                        >
                            GO
                        </Button>
                    </Item>
                    <Item sx={sx.itemsxGrow}>
                        <DropDownOA
                            label={`Branch State`}
                            id={'branchState'}
                            searchPlaceholder={`Search By Branch State`}
                            dataKey={`StateName`}
                            idKey={`StateID`}
                            data={removeDuplicatesFromArray(branchStateData, 'StateID') || []}
                            value={selBranchStateData?.StateName || ''}
                            defaultvalue={selBranchStateData?.StateName || ''}
                            onSelect={(obj) => { setSelBranchStateData(obj); setReportView(false) }}
                            width="100%"
                            nextId={'selectedBranch'}
                            prevId={'goButtonDate'}
                        />
                    </Item>
                    <Item sx={sx.itemsxGrow}>
                        <DropDownOA
                            id={'brnachCity'}
                            label={'Branch City'}
                            searchPlaceholder={`Search By Branch City`}
                            data={removeDuplicatesFromArray(branchCityData, 'CityID') || []}
                            defaultvalue={selBranchCityData?.CityName || ''}
                            onSelect={(val) => { setSelBranchCityData(val); setReportView(false) }}
                            dataKey={`CityName`}
                            idKey={`CityID`}
                            nextId={'generateButton'}
                            prevId={'paymentType'}
                            width="100%"
                        />
                    </Item>
                    <Item sx={sx.itemsxGrow}>
                        <DropDownOA
                            id={'selectedBranch'}
                            label={'Branch Name'}
                            searchPlaceholder={`Search By Branch`}
                            data={removeDuplicatesFromArray(branchData, 'BranchID') || []}
                            defaultvalue={selBranchData?.BranchName || ''}
                            onSelect={(val) => { setSelBranchData(val); setReportView(false) }}
                            dataKey={`BranchName`}
                            idKey={`BranchID`}
                            nextId={'generateButton'}
                            prevId={'paymentType'}
                            width="100%"
                        />
                    </Item>
                    <Item sx={sx.itemsx}
                    >
                        <SplitButton
                            options={['GENERATE NEW FORMAT', 'GENERATE OLD FORMAT']}
                            // hideToggle={false}
                            id1={'generateButton'}
                            onClick1={generateReport}
                        // onClick2={handleClick2}
                        />
                    </Item>
                </Box>
            </Item>
        </>
    )
}

export default BranchBalanceReportNew